import { UseMutationOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import userAPI from "../../api/userAPI";
import {
  PreferencesType,
  ResponseUser,
  UpdateUserPreferenceRequest,
} from "../../types/account/type";
import { userQueryKeys } from "./queries";
import { useSetRecoilState } from "recoil";
import { preferenceState } from "../../recoil/preference/preferenceAtom";
import { SpaceIdType } from "../../types/space";

interface UpdateUserPreferenceContext {
  previousUser?: ResponseUser;
  previousPreference?: PreferencesType;
  previousSelectedSpaceIds?: SpaceIdType[];
}

/**
 * 실제 preference 값을 업데이트 할 때에는 해당 Mutation이 아닌 usePreference 훅을 사용해야 합니다.
 * @param options
 * @returns
 */

export function useUpdateUserPreferenceMutation(
  options?: Omit<
    UseMutationOptions<
      ResponseUser,
      unknown,
      UpdateUserPreferenceRequest,
      UpdateUserPreferenceContext
    >,
    "mutationKey" | "mutationFn"
  >
) {
  const queryClient = useQueryClient();
  const setPreference = useSetRecoilState(preferenceState);

  return useMutation({
    mutationFn: (payload: Partial<PreferencesType>) =>
      userAPI.updateUserInfo({ preference: payload }),
    onMutate: async (payload: Partial<PreferencesType>) => {
      await queryClient.cancelQueries({ queryKey: userQueryKeys.all });

      // 이전 상태 저장
      const previousUser = queryClient.getQueryData<ResponseUser>(userQueryKeys.all);
      const previousPreference = previousUser?.preference;
      const previousSelectedSpaceIds = previousUser?.preference?.selectedSpaceIds;

      if (previousUser) {
        queryClient.setQueryData<ResponseUser>(userQueryKeys.all, {
          ...previousUser,
          preference: {
            ...previousUser.preference,
            ...payload,
          },
        });
      }

      return {
        previousUser,
        previousPreference,
        previousSelectedSpaceIds,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
    onError: (error, variables, context) => {
      // recoil 되돌리기
      if (context?.previousPreference) {
        setPreference(context.previousPreference);
      }

      // 쿼리 데이터 되돌리기
      if (context?.previousUser) {
        queryClient.setQueryData(userQueryKeys.all, context.previousUser);
      }
      // 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
    ...options,
  });
}
